import React, { useCallback, useMemo, useRef } from 'react'

import { FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  DatePickerV2,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { startDateOptions } from '../../../../utils/contractDurationOptions'
import { useStyles } from './ContractDurationCombinedForm.styles'
import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { SelectChangeEvent, Typography } from '@mui/material'
import { labraUIDateFormatForm } from '../OfferDetails/OfferDetails'
import { HelpDrawer } from '@labrav/common'
import { ScheduleType } from '../../../../modules/privateOffer/reducer'
import {
  awsMaxContractDurationMonths,
  awsMaxContractDurationYears,
} from '../../../../common/utils/constants'

export const ContractDurationOptions = [
  {
    label: 'Months',
    value: 'MONTHS',
  },
  {
    label: 'Years',
    value: 'YEARS',
  },
]
export type ContractDurationCombinedFormProps = {
  disabled: boolean
}

export const ContractDurationCombinedForm: React.FC<
  ContractDurationCombinedFormProps
> = ({ disabled }) => {
  const formikProps = useFormikContext() as FormikProps<CombinedFormState>
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps
  const { classes } = useStyles()
  const isFirstRender = useRef(true)
  const handleChangeContractDuration = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target
    const numericValue = value === '' ? '' : Number(value)
    setFieldTouched(
      'resellerAgreementData.contractDurationInMonthsForEndCustomer',
      true
    )
    await setFieldValue(
      'resellerAgreementData.contractDurationInMonthsForEndCustomer',
      numericValue
    )
    await setFieldValue('resellerAgreementData.installmentInfo', [])
    await setFieldValue('cppoData.installmentInfo', [])
    await setFieldValue('cppoData.flexiblePaymentType', ScheduleType.CUSTOM)
    await setFieldValue('resellerAgreementData.firstInstallmentDate', '')
  }

  const channelPrivateOfferContractStartDateField = {
    title: 'Contract start date',
    placeHolder: 'Contract start date',
    humanReadableKey: 'Contract start date',
    id: 'cppoData.variableStartDate',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    defaultValue: 'Offer acceptance date',
    dataType: 'enum' as DataType,
  }

  const channelPrivateOfferAgreementStartDateField = {
    title: 'Select a date',
    humanReadableKey: 'Select a date',
    placeHolder: 'Input',
    id: 'cppoData.agreementStartDate',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }
  const ContractDurationSelectField = {
    title: 'Duration type',
    placeHolder: 'Duration type',
    humanReadableKey: 'Duration type',
    id: 'resellerAgreementData.contractDurationTimeUnit',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    defaultValue: 'Duration type',
    dataType: 'enum' as DataType,
  }

  const handleChangeSelect = useCallback(
    (event: SelectChangeEvent) => {
      const selectedValues = event.target.value as string
      setFieldValue(
        'resellerAgreementData.contractDurationTimeUnit',
        selectedValues
      )
      setFieldTouched(
        'resellerAgreementData.contractDurationInMonthsForEndCustomer',
        false
      )
      setFieldValue(
        'resellerAgreementData.contractDurationInMonthsForEndCustomer',
        ''
      )
      setFieldValue('resellerAgreementData.installmentInfo', [])
      setFieldValue('cppoData.installmentInfo', [])
      setFieldValue('cppoData.flexiblePaymentType', ScheduleType.CUSTOM)
      setFieldValue('resellerAgreementData.firstInstallmentDate', '')
    },
    [values.resellerAgreementData.contractDurationInMonthsForEndCustomer]
  )
  const handleChangeVariableDateSelect = useCallback(
    (event: SelectChangeEvent) => {
      const selectedValues = event.target.value
      setFieldValue('cppoData.variableStartDate', selectedValues)
    },
    [values.cppoData.variableStartDate]
  )
  const durationText = useMemo(() => {
    return values.resellerAgreementData.contractDurationTimeUnit === 'MONTHS'
      ? 'Duration (in months)'
      : 'Duration (in years)'
  }, [values.resellerAgreementData.contractDurationTimeUnit])

  return (
    <div data-testid="Contract-duration-section-combined-form">
      <BoxV4
        heading={'Contract duration'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName="combinedFormContractDuration"
            headerTitle="Help & FAQs"
          />
        }
      >
        <div className={classes.sectionContainer}>
          <>
            <Typography className={classes.sectionHeading}>
              Specify the length of the contract. The price will switch back to
              public price after this duration.
            </Typography>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <SelectV4
                  field={{
                    ...channelPrivateOfferContractStartDateField,
                    options: startDateOptions,
                  }}
                  value={values.cppoData.variableStartDate}
                  onBlur={handleBlur}
                  onChange={e => handleChangeVariableDateSelect(e)}
                  isError={
                    !!(
                      get(touched, 'cppoData.variableStartDate,', false) &&
                      get(errors, 'cppoData.variableStartDate,', false)
                    )
                  }
                  errorText={
                    (!!get(touched, 'cppoData.variableStartDate,', false) &&
                      get(errors, 'cppoData.variableStartDate,', '')) ||
                    ''
                  }
                />
              </div>

              <div className={classes.inputWidth}>
                {values.cppoData.variableStartDate === false ? (
                  <DatePickerV2
                    format={labraUIDateFormatForm}
                    field={channelPrivateOfferAgreementStartDateField}
                    key={'cppoData.agreementStartDate'}
                    showErrorHelper={
                      !!touched.cppoData?.agreementStartDate &&
                      !isEmpty(errors.cppoData?.agreementStartDate)
                    }
                    fieldId={'cppoData.agreementStartDate'}
                    formik={
                      formikProps as unknown as FormikProps<
                        Record<string, unknown>
                      >
                    }
                    errorHelperText={
                      (!!touched.cppoData?.agreementStartDate &&
                        errors.cppoData?.agreementStartDate) ||
                      ''
                    }
                    views={['year', 'month', 'day']}
                    size="small"
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </>
        </div>
        <div className={classes.sectionContainer}>
          <>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <SelectV4
                  showInsideInfoIcon={true}
                  showInsideInfoIconTooltip={{
                    title: '',
                    content: 'Select the time unit for the contract duration.',
                  }}
                  field={{
                    ...ContractDurationSelectField,
                    options: ContractDurationOptions,
                  }}
                  value={values.resellerAgreementData.contractDurationTimeUnit}
                  onChange={e => handleChangeSelect(e)}
                  onBlur={handleBlur}
                  isError={
                    !!(
                      get(
                        touched,
                        'resellerAgreementData.contractDurationTimeUnit',
                        false
                      ) &&
                      get(
                        errors,
                        'resellerAgreementData.contractDurationTimeUnit',
                        false
                      )
                    )
                  }
                  errorText={
                    (!!get(
                      touched,
                      'resellerAgreementData.contractDurationTimeUnit',
                      false
                    ) &&
                      get(
                        errors,
                        'resellerAgreementData.contractDurationTimeUnit',
                        ''
                      )) ||
                    ''
                  }
                  // showInsideInfoIcon={true}
                  // showInsideInfoIconTooltip={{
                  //   title: '',
                  //   content: 'lorem ipsum ',
                  // }}
                />
              </div>

              {/* copy in cppo serviceLength */}
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="resellerAgreementData.contractDurationInMonthsForEndCustomer"
                  title={durationText}
                  type="number"
                  placeHolder={durationText}
                  value={
                    values.resellerAgreementData
                      .contractDurationInMonthsForEndCustomer as number
                  }
                  updatedValue={
                    values.resellerAgreementData
                      .contractDurationInMonthsForEndCustomer as number
                  }
                  maxNumber={
                    values.resellerAgreementData.contractDurationTimeUnit ===
                    'YEARS'
                      ? awsMaxContractDurationYears
                      : awsMaxContractDurationMonths
                  }
                  isError={
                    !!(
                      get(
                        touched,
                        'resellerAgreementData.contractDurationInMonthsForEndCustomer',
                        false
                      ) &&
                      get(
                        errors,
                        'resellerAgreementData.contractDurationInMonthsForEndCustomer',
                        false
                      )
                    )
                  }
                  onChange={handleChangeContractDuration}
                  isReadOnly={disabled}
                  isRequired={false}
                  errorText={
                    (!!get(
                      touched,
                      'resellerAgreementData.contractDurationInMonthsForEndCustomer',
                      false
                    ) &&
                      get(
                        errors,
                        'resellerAgreementData.contractDurationInMonthsForEndCustomer',
                        ' '
                      )) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                  keyToPrevent={['+', '-', 'e']}
                />
              </div>
            </div>
          </>
        </div>
      </BoxV4>
    </div>
  )
}
